import type { ReactNode } from 'react';
import React from 'react';
import type { FormikProps } from 'formik';
import { FieldArray, FormikProvider } from 'formik';
import type { ButtonV2Props, FlexProps } from '@withjoy/joykit';
import { Flex, ButtonV2, SpacingStack, Divider, TextV2, Box } from '@withjoy/joykit';
import type { Contacts, Context, IframeContacts, FieldVisibility } from '../../shared/types';
import { useFormController } from './Form.controller';
import { Wrapper, Footer } from '../components';
import { AddMember, ContactInfo, Header, MailingAddress, Tabs, TermsAndConditions } from './components';
import { useNameAffixSuggestions } from './components/NameAffixFields/useNameAffixSuggestions';
import { JoyLogoLoader } from '@shared/components/JoyLogoLoader';
import type { RoutesContextProps } from '@shared/core/router.helper/RoutesContext';

interface FormProps
  extends Readonly<{
    formik: FormikProps<Contacts> | FormikProps<IframeContacts>;
    context?: Context;
    isCollectHouseholdEnabled: boolean;
    isIframeVersion?: boolean;
    phoneNumberRule?: FieldVisibility;
    mailingAddressRule?: FieldVisibility;
    emailRule?: FieldVisibility;
    isRegistration?: boolean;
    routeHelpers?: RoutesContextProps;
    isMutationLoading?: boolean;
    hasMutationError?: boolean;
    e164PhoneNumber: { [key: number]: string };
    setE164PhoneNumber: React.Dispatch<React.SetStateAction<{ [key: number]: string }>>;
    hideTermsAndConditions?: boolean;
    formatIntlTelInputOnInit?: boolean;
    isMobileGuesList?: boolean;
    overrides?: {
      submitButtonProps?: ButtonV2Props;
      submitButtonLabel?: string;
      extraCTAs?: ReactNode;
      beforeButtons?: ReactNode;
      wrapperProps?: FlexProps;
      disableAddressValidation?: boolean;
      formMinHeight?: string;
      formHeader?: ReactNode;
    };
    isMailingAddressOpenedByDefault?: boolean;
    /**
     * When set, renders the optional Title (prefix) / Suffix inputs with
     * locale-keyed suggestions for this event. Off by default so the other
     * reuses of this form (registration, website-embedded forms, admin guest
     * list) stay unchanged.
     */
    nameAffixesEventId?: string | null;
    /**
     * Capability token authorizing the suggestions query on the public guest
     * collector (no session there). Admin surfaces omit it — the logged-in
     * event admin authorizes the query instead.
     */
    nameAffixesContactCollectionId?: string | null;
    isUpdateMode?: boolean;
    phoneInputResetKey?: number;
    showEnvelopeNameOverrideField?: boolean;
    phoneCountryCode?: string;
    // Optional caller-owned navigation for "advance to address validation",
    // used when Form is rendered outside a routing context (e.g. in a dialog).
    onNavigateToAddress?: () => void;
  }> {}

export const Form: React.FC<FormProps> = ({
  formik,
  context,
  isCollectHouseholdEnabled,
  isIframeVersion = false,
  phoneNumberRule = 'optional',
  mailingAddressRule = 'optional',
  emailRule = 'required',
  isRegistration = false,
  routeHelpers,
  isMutationLoading,
  hasMutationError,
  e164PhoneNumber,
  setE164PhoneNumber,
  hideTermsAndConditions = false,
  // Retained for caller API compatibility; no longer forwarded now that
  // IntlTelInputWrapper handles formatting on init itself.
  formatIntlTelInputOnInit: _formatIntlTelInputOnInit = true,
  isMobileGuesList = false,
  overrides,
  isMailingAddressOpenedByDefault = false,
  nameAffixesEventId,
  nameAffixesContactCollectionId,
  isUpdateMode = false,
  phoneInputResetKey,
  showEnvelopeNameOverrideField = false,
  onNavigateToAddress,
  phoneCountryCode = 'us',
}) => {
  const {
    tAdmin,
    handleOnSaveDetailsClick,
    errors: { firstNameError, lastNameError, emailError, address1Error, postalCodeError, cityError, regionError, countryError },
    handleOnCountryDropdownChange,
    isEmailInvalid,
    isPhoneNumberInvalidWithoutFocus,
    setIsPhoneNumberValid,
    phoneNumberTouched,
    setPhoneNumberTouched,
  } = useFormController(
    formik,
    isIframeVersion,
    e164PhoneNumber,
    setE164PhoneNumber,
    context?.coupleNames,
    routeHelpers,
    phoneNumberRule,
    mailingAddressRule,
    emailRule,
    overrides?.disableAddressValidation,
    onNavigateToAddress,
  );

  const nameAffixSuggestions = useNameAffixSuggestions(nameAffixesEventId, nameAffixesContactCollectionId ?? undefined);

  const hasValidPhoneNumber = formik.values.contacts.some((contact, index) => !!contact.phoneNumber && !isPhoneNumberInvalidWithoutFocus(index));
  const showSmsTerms = !hideTermsAndConditions && phoneNumberRule !== 'hidden' && (phoneNumberRule === 'required' || hasValidPhoneNumber);
  const buttonLabel = overrides?.submitButtonLabel ?? tAdmin.saveDetails();

  if (isMutationLoading && !isMobileGuesList) return <JoyLogoLoader loaderKey="contact-collector-form" />;

  return (
    <Wrapper
      wrapperOverrides={{
        // In the iframe (admin guest list), an ancestor can stretch the wrapper to a
        // viewport-derived height that Safari intermittently resolves short while the host
        // modal is mid-animation. Vertical centering (alignItems: center) then pushes the top
        // of a taller form out of view — the Safari-only "clipped/sliver" guest-detail bug.
        // Top-align so any overflow goes downward (visible and scrollable) instead of upward.
        ...(isIframeVersion && { alignItems: 'flex-start' }),
        ...overrides?.wrapperProps,
      }}
    >
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        paddingX={[isIframeVersion ? '40px' : '24px', null, 0]}
        width="100%"
        marginBottom={isIframeVersion ? '40px' : 0}
      >
        {!isIframeVersion && (
          <Header
            title={tAdmin.addYourInfo()}
            coupleNames={context?.coupleNames}
          />
        )}
        <FormikProvider
          key="contact-collector"
          value={formik}
        >
          <form
            style={{ width: '100%' }}
            onSubmit={e => {
              // prevent default form submit navigation behavior
              e.preventDefault();
            }}
          >
            <Box minHeight={overrides?.formMinHeight}>
              {overrides?.formHeader}
              <FieldArray name="contacts">
                {({ push, remove }) => (
                  <>
                    {isCollectHouseholdEnabled && (
                      <Tabs
                        formik={formik}
                        push={push}
                        isIframeVersion={isIframeVersion}
                      />
                    )}
                    {formik.values.contacts.map((contact, index) => (
                      <SpacingStack
                        key={index}
                        spacing={6}
                      >
                        <ContactInfo
                          formik={formik}
                          index={index}
                          errors={{ firstNameError, lastNameError, emailError }}
                          isEmailInvalid={isEmailInvalid}
                          remove={remove}
                          isIframeVersion={isIframeVersion}
                          phoneNumberRule={phoneNumberRule}
                          emailRule={emailRule}
                          isRegistration={isRegistration}
                          showNameAffixFields={!!nameAffixesEventId}
                          nameAffixSuggestions={nameAffixSuggestions}
                          isPhoneNumberInvalidWithoutFocus={isPhoneNumberInvalidWithoutFocus}
                          setIsPhoneNumberValid={setIsPhoneNumberValid}
                          phoneNumberTouched={phoneNumberTouched}
                          setPhoneNumberTouched={setPhoneNumberTouched}
                          phoneInputResetKey={phoneInputResetKey}
                          phoneCountryCode={phoneCountryCode}
                        />
                        {index === 0 && mailingAddressRule !== 'hidden' && (
                          <MailingAddress
                            showEnvelopeNameOverrideField={showEnvelopeNameOverrideField}
                            formik={formik}
                            index={index}
                            contact={contact}
                            handleOnCountryDropdownChange={handleOnCountryDropdownChange}
                            errors={{ address1Error, postalCodeError, cityError, regionError, countryError }}
                            isIframeVersion={isIframeVersion}
                            mailingAddressRule={mailingAddressRule}
                            isSectionOpenedByDefault={isMailingAddressOpenedByDefault}
                            isUpdateMode={isUpdateMode}
                          />
                        )}
                      </SpacingStack>
                    ))}
                    {isCollectHouseholdEnabled && (
                      <>
                        <Divider marginTop="2rem" />
                        <AddMember
                          push={push}
                          title={tAdmin.addMember()}
                          isIframeVersion={isIframeVersion}
                        />
                      </>
                    )}
                  </>
                )}
              </FieldArray>
              {isIframeVersion && (
                <TextV2
                  typographyVariant="label1"
                  fontWeight={800}
                  marginTop="16px"
                >
                  {tAdmin.requiredFields()}
                </TextV2>
              )}
            </Box>
            {overrides?.beforeButtons}
            <Flex
              marginTop="1.5rem"
              width="100%"
              columnGap="6"
            >
              {overrides?.extraCTAs}
              <ButtonV2
                intent="primary"
                shape="rounded"
                onClick={handleOnSaveDetailsClick}
                type="submit"
                fullWidth
                disabled={isMutationLoading && isMobileGuesList}
                {...overrides?.submitButtonProps}
              >
                {buttonLabel}
              </ButtonV2>
            </Flex>
            {showSmsTerms && <TermsAndConditions buttonLabel={buttonLabel} />}
          </form>
        </FormikProvider>
        {hasMutationError && (
          <TextV2
            typographyVariant="body3"
            color="red"
            marginTop="16px"
          >
            {tAdmin.contactError()}
          </TextV2>
        )}
        {!isIframeVersion && <Footer />}
      </Flex>
    </Wrapper>
  );
};
